import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { get, put } from "../../redux/ApiHelper";
import {
  DemoClassInterface,
  TeacherInterface,
  TeacherVideoReviewData,
} from "../../interfaces/teacher.interface";
import { useDispatch, useSelector } from "react-redux";
import { DispatchType } from "../../interfaces/redux.interface";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import { RootState } from "../../redux/store";
import { errorToast, successToast, warningToast } from "../../utils/toast.util";
import { FaFilePdf } from "react-icons/fa";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import VideoRejectModal from "./components/VideoRejectModal";
import VerificationStepsPanel from "../../components/verification/VerificationStepsPanel";
import { AccountVerificationStatus } from "../../interfaces/verification.interface";
import TeachingSetsList from "./components/TeachingSetsList";
import BankAccountSection from "./components/BankAccountSection";
import { TeachingSetInterface } from "../../interfaces/teaching-set.interface";
import { BankAccountInterface } from "../../interfaces/bank-account.interface";
import {
  buildAdminMapViewUrl,
  getCoordinatesFromPinLocation,
} from "../../utils/olaMap.util";

type RejectTarget =
  | { type: "intro" }
  | { type: "demo"; demoClassIndex: number; title: string };

const defaultRejection = { value: false, reason: "" };

const formatJoinedDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { dateStyle: "medium" })
    : "N/A";

const VideoStatusBadge = ({
  hasVideo,
  rejected,
  reason,
}: {
  hasVideo: boolean;
  rejected: boolean;
  reason?: string;
}) => {
  if (!hasVideo) return null;

  return (
  <div className="mt-2">
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        rejected
          ? "bg-red-500/20 text-red-300 border border-red-400/40"
          : "bg-green-500/20 text-green-300 border border-green-400/40"
      }`}
    >
      {rejected ? "Rejected" : "Approved"}
    </span>
    {rejected && reason ? (
      <p className="text-red-300 text-sm mt-2 text-left">
        <span className="font-semibold text-red-400">Reason:</span> {reason}
      </p>
    ) : null}
  </div>
  );
};

const TeacherDetails = () => {
  const dispatch: DispatchType = useDispatch();
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const { id } = useParams<{ id: string }>();
  const [teacher, setTeacher] = useState<TeacherInterface | null>(null);
  const [videoReview, setVideoReview] = useState<TeacherVideoReviewData | null>(
    null
  );
  const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);
  const [isRejectSubmitting, setIsRejectSubmitting] = useState(false);
  const [verification, setVerification] =
    useState<AccountVerificationStatus | null>(null);
  const [teachingSets, setTeachingSets] = useState<TeachingSetInterface[]>([]);
  const [bankAccount, setBankAccount] = useState<BankAccountInterface | null>(
    null
  );

  const isFranchiseAdmin = signedAdmin.type === "franchise";

  const fetchVideoReview = useCallback(async () => {
    if (!id || isFranchiseAdmin) return;
    try {
      const res = await get(`/admin/teacher-verification/videos/${id}`);
      setVideoReview(res.data);
    } catch {
      errorToast("Failed to load teacher videos for review");
    }
  }, [id, isFranchiseAdmin]);

  useEffect(() => {
    if (!id) return;
    dispatch(startLoading());
    Promise.all([
      get(`/teacher/profile/${id}`),
      get(`/admin/teacher/${id}/verification`),
      get(`/admin/teacher/${id}/teaching-sets`),
      get(`/admin/teacher/${id}/bank-account`, {}, {
        suppressNotFoundToast: true,
      }).catch(() => null),
    ])
      .then(([profileRes, verificationRes, teachingSetsRes, bankAccountRes]) => {
        setTeacher(profileRes);
        setVerification(verificationRes);
        setTeachingSets(teachingSetsRes.teachingSets ?? []);
        setBankAccount(bankAccountRes?.details ?? null);
      })
      .catch(() => {
        errorToast("Something went wrong! Please try again later");
      })
      .finally(() => {
        dispatch(stopLoading());
      });

    void fetchVideoReview();
    //eslint-disable-next-line
  }, [id, fetchVideoReview]);

  const handleVerify = async (
    field: "highestQualification" | "otherDocs" | "aadhar" | "pan",
    docIndex?: number
  ) => {
    if (!field || (field === "otherDocs" && docIndex === null)) {
      warningToast("Please provide all necessary information.");
      return;
    }
    dispatch(startLoading());
    await put(`/admin/teacher-verification/verify/${teacher?._id}`, {
      field,
      docIndex,
    })
      .then((res) => {
        successToast(res.message);
        setTeacher(res.data);
      })
      .catch(() => {
        errorToast("Error updating verification status");
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

  const handleRejectSubmit = async (reason: string) => {
    if (!id || !rejectTarget) return;
    setIsRejectSubmitting(true);
    try {
      if (rejectTarget.type === "intro") {
        const res = await put(
          `/admin/teacher-verification/reject-intro-video/${id}`,
          { reason }
        );
        successToast(res.message);
        setVideoReview((prev) =>
          prev
            ? {
                ...prev,
                isSelfIntroductionVideoRejectedByAdmin:
                  res.data.isSelfIntroductionVideoRejectedByAdmin,
              }
            : prev
        );
      } else {
        const res = await put(
          `/admin/teacher-verification/reject-demo-video/${id}`,
          { demoClassIndex: rejectTarget.demoClassIndex, reason }
        );
        successToast(res.message);
        setVideoReview((prev) =>
          prev ? { ...prev, demoClasses: res.data.demoClasses } : prev
        );
      }
      setRejectTarget(null);
    } catch {
      errorToast("Failed to reject video");
    } finally {
      setIsRejectSubmitting(false);
    }
  };

  const handleClearIntroRejection = async () => {
    if (!id) return;
    dispatch(startLoading());
    try {
      const res = await put(
        `/admin/teacher-verification/approve-intro-video/${id}`
      );
      successToast(res.message);
      setVideoReview((prev) =>
        prev
          ? {
              ...prev,
              isSelfIntroductionVideoRejectedByAdmin:
                res.data.isSelfIntroductionVideoRejectedByAdmin,
            }
          : prev
      );
    } catch {
      errorToast("Failed to clear intro video rejection");
    } finally {
      dispatch(stopLoading());
    }
  };

  const handleClearDemoRejection = async (demoClassIndex: number) => {
    if (!id) return;
    dispatch(startLoading());
    try {
      const res = await put(
        `/admin/teacher-verification/approve-demo-video/${id}`,
        { demoClassIndex }
      );
      successToast(res.message);
      setVideoReview((prev) =>
        prev ? { ...prev, demoClasses: res.data.demoClasses } : prev
      );
    } catch {
      errorToast("Failed to clear demo video rejection");
    } finally {
      dispatch(stopLoading());
    }
  };

  const renderDemoClassCard = (demoClass: DemoClassInterface, index: number) => {
    const rejection = demoClass.isRejectedByAdmin || defaultRejection;
    const isRejected = rejection.value === true;
    const hasVideo = Boolean(demoClass.videoUrl);

    return (
      <div
        key={`${demoClass.title}-${index}`}
        className="rounded-xl border border-gray-600 bg-gray-800/60 p-4"
      >
        <h3 className="text-yellow-400 font-semibold text-left mb-3">
          {demoClass.title || `Demo class ${index + 1}`}
        </h3>
        {demoClass.videoUrl ? (
          <video
            controls
            className="w-full max-h-64 rounded-lg bg-black border border-gray-700"
            src={demoClass.videoUrl}
          />
        ) : (
          <p className="text-gray-400 text-sm">No video URL available.</p>
        )}
        <VideoStatusBadge
          hasVideo={hasVideo}
          rejected={isRejected}
          reason={rejection.reason}
        />
        {hasVideo ? (
        <div className="flex flex-wrap gap-2 mt-4">
          {!isRejected ? (
            <button
              type="button"
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
              onClick={() =>
                setRejectTarget({
                  type: "demo",
                  demoClassIndex: index,
                  title: demoClass.title || `Demo class ${index + 1}`,
                })
              }
            >
              Reject
            </button>
          ) : (
            <button
              type="button"
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              onClick={() => handleClearDemoRejection(index)}
            >
              Clear rejection
            </button>
          )}
        </div>
        ) : null}
      </div>
    );
  };

  if (!teacher) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  const introRejection =
    videoReview?.isSelfIntroductionVideoRejectedByAdmin || defaultRejection;
  const isIntroRejected = introRejection.value === true;
  const hasIntroVideo = Boolean(videoReview?.selfIntroductionVideo);

  const teachingAddressMap = (() => {
    for (const set of teachingSets) {
      const location =
        set.location && typeof set.location !== "string" ? set.location : null;
      if (location?.isActive === false) continue;
      const coords = getCoordinatesFromPinLocation(location);
      if (coords) {
        return { ...coords, label: location?.formattedAddress };
      }
    }
    for (const set of teachingSets) {
      const location =
        set.location && typeof set.location !== "string" ? set.location : null;
      const coords = getCoordinatesFromPinLocation(location);
      if (coords) {
        return { ...coords, label: location?.formattedAddress };
      }
    }
    return null;
  })();

  return (
    <div className="flex flex-col p-4 teacher-details">
      <div className="flex flex-col rounded-lg shadow-lg">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-8 p-4 h-fit">
          <img
            src={teacher.profilePicture}
            alt={`${teacher.name}'s profile`}
            className="w-32 h-32 rounded-full shadow-md object-cover border-4 border-yellow-500"
          />
          <div>
            <h1 className="text-3xl font-bold text-yellow-500 text-left">
              {teacher.name}
            </h1>
            <p className="text-gray-200 mt-2">
              {teacher?.about || "No profile overview available."}
            </p>
            <p className="text-gray-400 text-sm mt-1 text-left">
              Joined: {formatJoinedDate(teacher.createdAt)}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span
                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                  teacher.isAccountVerified
                    ? "bg-green-500/20 text-green-300 border border-green-400/40"
                    : "bg-amber-500/20 text-amber-300 border border-amber-400/40"
                }`}
              >
                {teacher.isAccountVerified ? "Account Verified" : "Not Verified"}
              </span>
              <span
                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                  teacher.isActive
                    ? "bg-green-500/20 text-green-300 border border-green-400/40"
                    : "bg-red-500/20 text-red-300 border border-red-400/40"
                }`}
              >
                {teacher.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        <div className="px-4 pb-4">
          <VerificationStepsPanel
            verification={verification}
            renderStepExtra={(step) => {
              if (step.key === "teachingSet") {
                return <TeachingSetsList sets={teachingSets} />;
              }
              if (step.key === "teachingAddress" && teachingAddressMap) {
                return (
                  <div className="mt-3 border-t border-gray-700 pt-3">
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          buildAdminMapViewUrl(
                            teachingAddressMap.lat,
                            teachingAddressMap.lng,
                            14,
                            teachingAddressMap.label
                          ),
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-400/40 hover:bg-amber-500/30 transition"
                    >
                      View on Map
                    </button>
                  </div>
                );
              }
              return null;
            }}
          />
        </div>

        <BankAccountSection bankAccount={bankAccount} />

        {!isFranchiseAdmin && (
          <div className="px-4 pb-4">
            <h2 className="text-xl font-semibold text-yellow-500 text-left mb-4">
              Video Review
            </h2>
            <div className="space-y-6">
              <div className="rounded-xl border border-gray-600 bg-gray-800/60 p-4">
                <h3 className="text-yellow-400 font-semibold text-left mb-3">
                  Self introduction video
                </h3>
                {videoReview?.selfIntroductionVideo ? (
                  <video
                    controls
                    className="w-full max-h-80 rounded-lg bg-black border border-gray-700"
                    src={videoReview.selfIntroductionVideo}
                  />
                ) : (
                  <p className="text-gray-400 text-sm">
                    No self introduction video uploaded.
                  </p>
                )}
                <VideoStatusBadge
                  hasVideo={hasIntroVideo}
                  rejected={isIntroRejected}
                  reason={introRejection.reason}
                />
                {hasIntroVideo ? (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {!isIntroRejected ? (
                      <button
                        type="button"
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                        onClick={() => setRejectTarget({ type: "intro" })}
                      >
                        Reject
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                        onClick={handleClearIntroRejection}
                      >
                        Clear rejection
                      </button>
                    )}
                  </div>
                ) : null}
              </div>

              <div>
                <h3 className="text-yellow-400 font-semibold text-left mb-3">
                  Demo class videos
                </h3>
                {videoReview?.demoClasses?.length ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {videoReview.demoClasses.map(renderDemoClassCard)}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">
                    No demo class videos uploaded.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="md:flex p-4 gap-4">
          <div className="w-full md:w-50 text-left">
            <h2 className="text-xl font-semibold text-yellow-500">
              Highest Qualification
            </h2>
            <div className="mt-4 space-y-3">
              {teacher?.highestQualification ? (
                <div className="text-gray-200 space-y-2">
                  <p>
                    <span className="font-semibold text-yellow-400">
                      Institute:
                    </span>{" "}
                    {teacher?.highestQualification?.instituteName}
                  </p>
                  <p>
                    <span className="font-semibold text-yellow-400">
                      Qualification:
                    </span>{" "}
                    {teacher?.highestQualification?.qualificationLabel}
                  </p>
                  <p>
                    <span className="font-semibold text-yellow-400">
                      Field of Study:
                    </span>{" "}
                    {teacher?.highestQualification?.fieldOfStudy}
                  </p>
                  <p>
                    <span className="font-semibold text-yellow-400">
                      Year of Completion:
                    </span>{" "}
                    {teacher?.highestQualification?.yearOfCompletion}
                  </p>
                  <p>
                    <span className="font-semibold text-yellow-400">
                      Grade/Percentage:
                    </span>{" "}
                    {teacher?.highestQualification?.gradeOrPercentage}
                  </p>
                  <p>
                    <span className="font-semibold text-yellow-400">
                      Degree:
                    </span>{" "}
                    {teacher?.highestQualification?.degreeName}
                  </p>
                  <div className="flex flex-col">
                    <p>
                      <span className="font-semibold text-yellow-400">
                        Certificate:
                      </span>
                    </p>
                    <a
                      href={teacher?.highestQualification?.docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2"
                    >
                      {teacher?.highestQualification?.docUrl
                        ?.toLowerCase()
                        .endsWith(".pdf") ? (
                        <div>
                          <FaFilePdf className="text-red-400" />
                          <p>View PDF document</p>
                        </div>
                      ) : (
                        <img
                          src={teacher?.highestQualification?.docUrl}
                          alt="Document thumbnail"
                          className="highest-qualification-img object-cover rounded-sm"
                        />
                      )}
                    </a>
                  </div>
                  {teacher?.highestQualification?.isVerified ? (
                    <p>
                      <span>Verified</span>{" "}
                      <IoMdCheckmarkCircleOutline className="text-green-500 ml-2" />
                    </p>
                  ) : (
                    <button
                      className="px-4 py-2 mt-2 bg-red-500 text-white rounded hover:bg-red-600"
                      onClick={() => handleVerify("highestQualification")}
                    >
                      Verify Qualification
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-gray-200">
                  No highest qualification available.
                </p>
              )}
            </div>

            {teacher?.otherDocs?.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-yellow-500">
                  Other Documents
                </h2>
                <div className="mt-4 space-y-4">
                  {teacher.otherDocs.map((doc, index) => (
                    <div key={index} className="flex flex-col gap-2">
                      <a
                        href={doc.docUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2"
                      >
                        {doc.docUrl?.toLowerCase().endsWith(".pdf") ? (
                          <div>
                            <FaFilePdf className="text-red-400" />
                            <p>View PDF document</p>
                          </div>
                        ) : (
                          <img
                            src={doc.docUrl}
                            alt="Document thumbnail"
                            className="highest-qualification-img object-cover rounded-sm"
                          />
                        )}
                      </a>
                      {doc.isVerified ? (
                        <p>
                          <span>Verified</span>{" "}
                          <IoMdCheckmarkCircleOutline className="text-green-500 ml-2" />
                        </p>
                      ) : (
                        <button
                          className="px-4 py-2 mt-2 bg-red-500 text-white rounded hover:bg-red-600"
                          onClick={() => handleVerify("otherDocs", index)}
                        >
                          Verify Certificates
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="w-full md:w-50 text-left">
            <h2 className="text-xl font-semibold text-yellow-500">Profile</h2>
            <div className="mt-4 space-y-3 text-gray-100">
              <p>
                <span className="font-semibold text-yellow-400">Email:</span>{" "}
                {teacher?.email || "N/A"}
              </p>
              <p>
                <span className="font-semibold text-yellow-400">Phone:</span>{" "}
                {teacher?.phoneNumber || "N/A"}
              </p>
              <p className="flex">
                <span className="font-semibold text-yellow-400 mr-2">
                  Aadhar:
                </span>{" "}
                {teacher?.aadhar?.number || "N/A"}{" "}
                {teacher?.aadhar?.isVerified ? (
                  <span className="text-green-500 ml-2 my-auto flex">
                    Verified
                    <IoMdCheckmarkCircleOutline className="text-green-500 ml-2 my-auto" />
                  </span>
                ) : (
                  <span
                    className="ml-2 cursor-pointer text-red-500"
                    onClick={() => handleVerify("aadhar")}
                  >
                    Mark as Verified
                  </span>
                )}
              </p>
              <p className="flex">
                <span className="font-semibold text-yellow-400 mr-2">Pan:</span>{" "}
                {teacher?.pan?.number || "N/A"}
                {teacher?.pan?.isVerified ? (
                  <span className="text-green-500 ml-2 my-auto flex">
                    Verified
                    <IoMdCheckmarkCircleOutline className="text-green-500 ml-2 my-auto" />
                  </span>
                ) : (
                  <span
                    className="ml-2 cursor-pointer text-red-500"
                    onClick={() => handleVerify("pan")}
                  >
                    Mark as Verified
                  </span>
                )}
              </p>
              <p>
                <span className="font-semibold text-yellow-400">Subjects:</span>{" "}
                {teacher?.subjects?.join(", ") || "N/A"}
              </p>
              <p>
                <span className="font-semibold text-yellow-400">
                  Experience:
                </span>{" "}
                {teacher?.yearOfExperience} years
              </p>
              <p>
                <span className="font-semibold text-yellow-400">Rating:</span>{" "}
                {teacher.averageRating
                  ? `${teacher.averageRating.toFixed(1)} / 5`
                  : "No Ratings Yet"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <VideoRejectModal
        isOpen={!!rejectTarget}
        title={
          rejectTarget?.type === "intro"
            ? "Reject self introduction video"
            : `Reject ${rejectTarget?.title || "demo video"}`
        }
        onClose={() => setRejectTarget(null)}
        onSubmit={handleRejectSubmit}
        isSubmitting={isRejectSubmitting}
      />
    </div>
  );
};

export default TeacherDetails;
